import { useState } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa6";
import { useFormik } from "formik";
import { object, ref as yupRef, string } from "yup";
import toast from "react-hot-toast";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import AuthHeroSection from "./AuthHeroSection";
import AuthLangButton from "../../components/authLangButton/AuthLangButton";
import { useTranslation } from "react-i18next";

export default function ResetPassword() {
  const [showPassword, setShowPassword] = useState(false);
  const [showRePassword, setShowRePassword] = useState(false);

  const passwordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
  const baseUrl = import.meta.env.VITE_BASE_URL;
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token");
  const isRTL = i18n.language === "ar";

  async function resetPassword() {
    const toastId = toast.loading(t("resetPassword.changingPassword") as string);
    try {
      await axios.put(
        `${baseUrl}/api/dashboard/users/reset-password?locale=en`,
        {
          password: formik.values.password,
          passwordConfirmation: formik.values.rePassword,
        },
        {
          headers: { Authorization: token },
        }
      );
      toast.success(t("resetPassword.passwordChanged") as string);
      navigate("/login");
    } catch (error) {
      console.error(error);
      toast.error("An unexpected error occurred");
    } finally {
      toast.dismiss(toastId);
    }
  }

  const validationSchema = object({
    password: string()
      .min(8, t("resetPassword.passwordTooShort") as string)
      .required(t("resetPassword.passwordRequired") as string)
      .matches(passwordRegex, t("resetPassword.passwordPattern") as string),
    rePassword: string()
      .required(t("resetPassword.rePasswordRequired") as string)
      .oneOf([yupRef("password")], t("resetPassword.passwordsNotMatch") as string),
  });

  const formik = useFormik({
    initialValues: {
      password: "",
      rePassword: "",
    },
    validationSchema,
    onSubmit: resetPassword,
  });

  return (
    <div className="xl:grid xl:grid-cols-12">
      <form
        onSubmit={formik.handleSubmit}
        className="h-[75vh] relative mt-10 flex justify-between w-[90%] md:w-[60%] xl:w-[70%] mx-auto flex-col col-span-4"
      >
        <AuthLangButton />
        <div className="flex flex-col gap-5 w-full rounded-md">
          <div className="flex flex-col gap-2">
            <h1 className="my-2 text-xl font-semibold">
              {t("resetPassword.title")} 🔒
            </h1>
            <span className="text-slate-400">
              {t("resetPassword.enterNewPassword")}
            </span>
          </div>

          {/* Password field */}
          <div className="flex flex-col gap-2 relative w-full">
            <label className="font-semibold">{t("resetPassword.enterRePassword")}</label>
            <input
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="password"
              type={showPassword ? "text" : "password"}
              className="input"
              placeholder={t("resetPassword.enterNewPassword") as string}
            />
            <span
              className={`absolute ${isRTL ? "left-2" : "right-2"} bottom-3 text-slate-400 cursor-pointer`}
              onClick={() => setShowPassword((prev) => !prev)}
            >
              {showPassword ? <FaRegEye /> : <FaRegEyeSlash />}
            </span>
          </div>
          {formik.errors.password && formik.touched.password && (
            <span className="text-red-500 text-sm me-auto">
              {formik.errors.password}
            </span>
          )}

          {/* Re-password field */}
          <div className="flex flex-col gap-2 relative w-full">
            <label className="font-semibold">{t("resetPassword.rePassword")}</label>
            <input
              value={formik.values.rePassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="rePassword"
              type={showRePassword ? "text" : "password"}
              className="input"
              placeholder={t("resetPassword.rePassword") as string}
            />
            <span
              className={`absolute ${isRTL ? "left-2" : "right-2"} bottom-3 text-slate-400 cursor-pointer`}
              onClick={() => setShowRePassword((prev) => !prev)}
            >
              {showRePassword ? <FaRegEye /> : <FaRegEyeSlash />}
            </span>
          </div>
          {formik.errors.rePassword && formik.touched.rePassword && (
            <span className="text-red-500 text-sm me-auto">
              {formik.errors.rePassword}
            </span>
          )}

          <button type="submit" className="my-btn">
            {t("resetPassword.title")}
          </button>
          <Link to="/login" className="text-sidebar mx-auto flex items-center gap-2">
            {t("resetPassword.backToLogin")}
            {isRTL ? <FaChevronLeft className="text-sm" /> : <FaChevronRight className="text-sm" />}
          </Link>
        </div>
      </form>
      <AuthHeroSection />
    </div>
  );
}
